import { Injectable } from '@angular/core';
import { fromJS, List } from 'immutable';
import {
  has,
  identity,
  isBoolean,
  isNumber,
  keys,
  pickBy,
  reduce,
} from 'lodash';
import {
  ALL_LIST_FILTER,
  IFetchFilterKey,
  ISearchParams,
  SearchParamsEnum,
  SortByEnum,
  SortOrderEnum,
} from '../entity-data/entity-data.data';
import {
  getInContent,
  IS_DIRTY_KEY,
  IS_NEW_KEY,
  setInContent,
} from '../entity-data/entity.model';
import { Settings } from '../settings-data/settings.model';
import { DateTimeService, HOUR_AS_SECONDS } from 'i2g-ng-utils';
import {
  ALL_YEARS,
  APPOINTMENT_LIST_SEARCH_PARAMS_KEY,
} from './appointment-data.data';
import { Appointment } from './models/appointment.model';
import { EntityDataBuilder } from '../entity-data/entity-data.builder';
import { UrlService } from '../shared/services/url/url.service';
import { EntityTypesEnum } from '../../app.enum';

@Injectable()
export class AppointmentDataBuilder extends EntityDataBuilder<Appointment> {
  model = Appointment;
  entityType: EntityTypesEnum = EntityTypesEnum.appointment;
  yearFilterKey: string = SearchParamsEnum.year;
  listFilterKeys: IFetchFilterKey = {
    [ALL_LIST_FILTER]: APPOINTMENT_LIST_SEARCH_PARAMS_KEY,
  };
  refreshFiltersDelay = 0;

  constructor(
    private _dateTimeService: DateTimeService,
    urlService: UrlService
  ) {
    super(urlService);
  }

  buildEntity(
    props: any = {},
    settings: Settings,
    isDirty?: boolean,
    isNew?: boolean
  ): Appointment {
    const localeKey = Appointment.CONTENT_KEYS.LOCALE;
    const startDateKey = Appointment.CONTENT_KEYS.START_DATE;
    const durationKey = Appointment.CONTENT_KEYS.DURATION;

    props = props && props.toMap ? props.toMap() : fromJS(props || {});

    const appointmentProps = props.withMutations((map) => {
      // Default locale
      if (!getInContent(map, localeKey)) {
        setInContent(map, localeKey, settings.documentLocale);
      }

      // Default start date and duration
      if (!getInContent(map, startDateKey)) {
        const now = this._dateTimeService.getNow(null, true);
        const roundedDate = this._dateTimeService.roundTime(
          now,
          30,
          null,
          true
        );

        setInContent(map, startDateKey, roundedDate);
        setInContent(map, durationKey, HOUR_AS_SECONDS);
      }

      if (isBoolean(isDirty)) {
        map.set(IS_DIRTY_KEY, isDirty);
      }

      if (isBoolean(isNew)) {
        map.set(IS_NEW_KEY, isNew);
      }
    });

    return new Appointment(appointmentProps);
  }

  buildEntities(array) {
    return List<Appointment>(
      array ? array.map(this.buildEntity.bind(this)) : []
    );
  }

  buildSortItemsComparatorFn(
    sortBy: SortByEnum,
    sortOrder: SortOrderEnum
  ): (itemA: Appointment, itemB: Appointment) => number {
    const order = sortOrder === SortOrderEnum.asc ? 1 : -1;
    const durationSort = (a: Appointment, b: Appointment): number => {
      const durationA = a.duration || 0;
      const durationB = b.duration || 0;

      if (durationA > durationB) {
        return order;
      } else if (durationA < durationB) {
        return order * -1;
      } else {
        return 0;
      }
    };
    const startDateSort = (a: Appointment, b: Appointment): number => {
      const startDateA = this._dateTimeService.getMilliseconds(
        this._dateTimeService.formatDate(a.startDate, null),
        null
      );
      const startDateB = this._dateTimeService.getMilliseconds(
        this._dateTimeService.formatDate(b.startDate, null),
        null
      );

      if (startDateA > startDateB) {
        return order;
      } else if (startDateA < startDateB) {
        return order * -1;
      } else {
        return 0;
      }
    };
    const billingNameSort = (a: Appointment, b: Appointment) => {
      const billingNameA = (a.billingName || '').toLowerCase();
      const billingNameB = (b.billingName || '').toLowerCase();

      if (billingNameA > billingNameB) {
        return order;
      } else if (billingNameA < billingNameB) {
        return order * -1;
      } else {
        return 0;
      }
    };

    return (a: Appointment, b: Appointment) => {
      switch (sortBy) {
        case SortByEnum.client_name:
          return (
            billingNameSort(a, b) ||
            (order === 1 ? startDateSort(a, b) : startDateSort(b, a)) || // keep startDateSort asc
            (order === 1 ? durationSort(a, b) : durationSort(b, a)) || // keep durationSort asc
            0
          );
        case SortByEnum.start_date:
          return (
            startDateSort(a, b) ||
            (order === 1 ? durationSort(a, b) : durationSort(b, a)) || // keep durationSort asc
            (order === 1 ? billingNameSort(a, b) : billingNameSort(b, a)) || // keep billingNameSort asc
            0
          );
        default:
          return null;
      }
    };
  }

  buildListItemsFilterFn(
    searchParams: ISearchParams
  ): (entity: Appointment) => boolean {
    const year = searchParams[this.yearFilterKey];
    const month = searchParams[SearchParamsEnum.month];
    const yearFilter =
      year && year !== ALL_YEARS
        ? (appointment: Appointment) =>
            this._dateTimeService.parse(appointment.startDate).year === year
        : (appointment: Appointment) => true;
    const monthFilter = month
      ? (appointment: Appointment) =>
          this._dateTimeService.parse(appointment.startDate).month + 1 === month
      : (appointment: Appointment) => true;

    return (appointment: Appointment) =>
      monthFilter(appointment) && yearFilter(appointment);
  }

  buildListFilter(entity: any): string {
    return ALL_LIST_FILTER;
  }

  buildSearchParams(
    currentSearchParams: ISearchParams,
    newSearchParams: ISearchParams,
    listFilter?: string
  ): ISearchParams {
    const commonParams = super.buildSearchParams(
      currentSearchParams,
      newSearchParams
    );
    const month =
      newSearchParams[SearchParamsEnum.month] ||
      currentSearchParams[SearchParamsEnum.month];
    const result = {
      ...commonParams,
      [SearchParamsEnum.month]: month,
    };

    return pickBy(result, identity);
  }

  validateSearchParams(searchParams: ISearchParams, filter: string) {
    const validParams = {
      [SearchParamsEnum.sort_by]: (a) =>
        [SortByEnum.client_name, SortByEnum.start_date].includes(a),
      [SearchParamsEnum.sort_order]: (a) =>
        [SortOrderEnum.asc, SortOrderEnum.desc].includes(a),
      [SearchParamsEnum.year]: isNumber,
    };
    const requiredKeys = keys(validParams).every((key) =>
      has(searchParams, key)
    );
    const validValues = reduce(
      searchParams,
      (result, value, key) =>
        result && has(validParams, key) && validParams[key](value),
      true
    );

    return requiredKeys && validValues;
  }
}
